<template>
  <div
    class="src-panel"
    :style="{ width: panelWidth + 'px' }"
  >
    <div
      class="src-resize-handle"
      title="Drag to resize"
      @mousedown="startResize"
    />

    <!-- Mirrors the reopen tab shown once the panel is hidden (Chatbot.vue's
         .src-tab--open) — same look, opposite arrow, pinned to this panel's
         own left edge instead of the screen edge so it tracks panelWidth. -->
    <button
      class="src-tab src-tab--close"
      title="Hide context"
      :style="{ right: panelWidth + 'px' }"
      @click="$emit('close')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ><polyline points="9 6 15 12 9 18" /></svg>
      <span>Context</span>
    </button>

    <div class="src-panel-hdr">
      <div class="flex items-center gap-2">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.83l-8.57 8.57a2 2 0 1 1-2.83-2.83l8.49-8.48" />
        </svg>
        <span class="src-panel-title">Context</span>
        <span
          v-if="docs.length"
          class="src-count"
        >{{ docs.length }} {{ docs.length === 1 ? 'doc' : 'docs' }}</span>
      </div>
    </div>

    <div class="src-panel-body">
      <!-- Player question(s): one block per player — spider chart + reports.
           Comparing two players stacks a second block underneath. -->
      <div
        v-if="players.length"
        class="player-row"
      >
        <player-block
          v-for="p in players"
          :key="p.player"
          :source="p"
          :org="org"
        />
      </div>

      <!-- Cited knowledge documents, one-at-a-time carousel. Sits below the
           player row when both are present; fills the panel when alone. -->
      <doc-carousel
        v-if="docs.length"
        :docs="docs"
        :compact="players.length > 0"
      />
    </div>
  </div>
</template>

<script>
import PlayerBlock from './PlayerBlock.vue'
import DocCarousel from './DocCarousel.vue'

const MIN_WIDTH = 320
// Absolute ceiling the user can drag the panel to. Kept independent of the
// row width: a cap defined as a ratio of the row (e.g. 20%) collapsed to
// exactly MIN_WIDTH on any row under 1600px wide, which made the drag
// handle look broken — clamp() always snapped straight back to 320 no
// matter which way you dragged.
const MAX_WIDTH = 640

export default {
  components: {
    PlayerBlock,
    DocCarousel
  },

  props: {
    sources: {
      type: Array,
      required: true
    },
    // The active chat's data/knowledge org (Chatbot.vue's `org`) — passed down
    // to player-block so its getRadar/getPlayerReports calls resolve the SAME
    // org the conversation is actually using, instead of the backend's
    // process-default org.
    org: {
      type: String,
      default: null
    },
    // Width (px) remembered by the parent across the panel being closed and
    // reopened. Null the first time it's ever shown, when it defaults to the
    // maximum allowed width (see MAX_WIDTH / clamp) instead of a narrow guess.
    width: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      panelWidth: this.width || MIN_WIDTH,
      dragging: false
    }
  },

  computed: {
    players () {
      return this.sources.filter(s => s.type === 'radar')
    },
    docs () {
      return this.sources.filter(s => s.type === 'document')
    }
  },

  mounted () {
    if (!this.width) {
      // First-ever open (no remembered width yet): start at the maximum
      // allowed width rather than a narrow default, so the panel doesn't
      // need a manual resize every time it's opened for the first time.
      this.panelWidth = this.clamp(MAX_WIDTH)
      this.$emit('resize', this.panelWidth)
    }
    window.addEventListener('resize', this.handleWindowResize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleWindowResize)
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopResize)
  },

  methods: {
    // The flex row shared with .chat-col — the container the default width
    // and the drag ceiling are both measured against.
    rowWidth () {
      const row = this.$el && this.$el.parentElement
      return row ? row.clientWidth : window.innerWidth
    },

    clamp (px) {
      const max = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, this.rowWidth() * 0.5))
      return Math.min(Math.max(px, MIN_WIDTH), max)
    },

    handleWindowResize () {
      const clamped = this.clamp(this.panelWidth)
      if (clamped !== this.panelWidth) {
        this.panelWidth = clamped
        this.$emit('resize', this.panelWidth)
      }
    },

    startResize (e) {
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartWidth = this.panelWidth
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopResize)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      e.preventDefault()
    },

    onDrag (e) {
      if (!this.dragging) return
      // The handle sits on the panel's left edge, so dragging left (negative
      // dx) widens the panel.
      const dx = e.clientX - this.dragStartX
      this.panelWidth = this.clamp(this.dragStartWidth - dx)
    },

    stopResize () {
      if (!this.dragging) return
      this.dragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopResize)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.$emit('resize', this.panelWidth)
    }
  }
}
</script>
